/**
 * Notification deadlines (13-27, D-13-L-11): `AwareAt` + the reportability decision → `NotificationDueAt`, plus the
 * due instant of each notice the notification panel records.
 *
 * `NotificationDueAt` drives C3's Limb B escalation and the `Notifications due` view, so a wrong deadline silences an
 * escalation. Every rule below is taken from its `12.1-OBLIGATIONS.md` row (the period cell verbatim, and the
 * instrument section as that file records it), never from memory; `notificationDeadlines.test.ts` reads the file and
 * checks each citation. Where a period can be read more than one way, the **earliest** instant is used, so a due time
 * is never later than the strictest reading (D-13-27-2); a reading still awaiting a ruling is marked `provisional`.
 *
 * - **Instants** are computed in UTC from the stored instant. "24 hours" is 24 elapsed hours, even across a daylight
 *   saving change. Calendar days and business days are counted on Melbourne calendar days through `Intl`
 *   (`fieldValues.melbourneWallOf` / `melbourneWallToInstant`); there is no date library and no fixed offset.
 * - **Business days** are Melbourne Monday to Friday. The app has no public-holiday calendar (the D-13-11-6
 *   precedent), so a public holiday is counted as a business day: the due date may be early, never late (D-13-27-1).
 * - **Choice values** (`SIRS-P1`, `24 hours`, `Yes` …) come from the live field set through `liveChoice`, never retyped.
 *   The frozen reporting column is never read (D-12.1-04-C).
 *
 * Pure: no `Date.now()`, no React, no Graph.
 */

import { liveChoice } from '../config/fieldset/live';
import { cleanText, formatInstant, melbourneDayOf, melbourneWallOf, melbourneWallToInstant, toInstant } from './fieldValues';

// ---------------------------------------------------------------------------
// The live values the rules key on
// ---------------------------------------------------------------------------

const [PENDING, NOT_REPORTABLE, SIRS_P1, SIRS_P2, NDIS_REPORTABLE, WORKSAFE, OTHER] = liveChoice(
  'incidents',
  'ReportabilityDecision',
  'Pending',
  'NotReportable',
  'SIRS-P1',
  'SIRS-P2',
  'NDIS-Reportable',
  'WorkSafe',
  'Other',
);
const [CLASS_24H, CLASS_5BD] = liveChoice('incidents', 'NdisNoticeClass', '24 hours', '5 business days');
const [WORKSAFE_YES] = liveChoice('incidents', 'WorkSafeNotifiable', 'Yes');

/** The decisions that start a notification clock (every live value except `Pending` and `NotReportable`). */
export const REPORTABLE_DECISIONS: readonly string[] = [SIRS_P1, SIRS_P2, NDIS_REPORTABLE, WORKSAFE, OTHER];

/** The "no deadline" decisions (FIELDSET §1.1: `Pending` is undetermined; `NotReportable` starts no clock). */
export const NO_DEADLINE_DECISIONS: readonly string[] = [PENDING, NOT_REPORTABLE];

/** `Pending` and `NotReportable`, by name, for the engine and the panels. */
export const PENDING_DECISION = PENDING;
export const NOT_REPORTABLE_DECISION = NOT_REPORTABLE;

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

/**
 * The regulator a deadline belongs to (`other`: the `Other` decision, which has no rule in the matrix; `whs`: 13-28's
 * workplace-injury duties under the WIRC Act, a claim, a certificate, a mental injury claim and return to work).
 */
export type Regime = 'ndis' | 'sirs' | 'worksafe' | 'whs' | 'other';

export type RuleKey =
  | 'sirs-p1'
  | 'sirs-p2'
  | 'sirs-p1-additional'
  | 'sirs-final-report'
  | 'ndis-24h'
  | 'ndis-24h-further'
  | 'ndis-5bd'
  | 'ndis-final-report'
  | 'worksafe'
  | 'worksafe-written-record'
  | 'whs-claim-forward'
  | 'whs-medical-certificate-forward'
  | 'whs-mental-injury-notify'
  | 'whs-rtw-planning'
  | 'other';

/** One rule, with its citation. */
export interface DeadlineRuleSpec {
  key: RuleKey;
  regime: Regime;
  /** The notice column this deadline is for (`null` for the placeholder). */
  notice: string | null;
  /** The `12.1-OBLIGATIONS.md` row the period is taken from (`null` only for the placeholder). */
  obRow: string | null;
  /** Other rows that state the same duty (the "… and when" rows, the class row). */
  alsoRows?: readonly string[];
  /** The instrument and section, as the row's instrument cell records them. */
  section: string;
  /** The row's period cell, verbatim. */
  period: string;
  /** How the due time is worked out, in plain words (the panels show it). */
  reading: string;
  /** A caveat the panels show (the public-holiday rule). */
  note?: string;
  /** Set when the reading is a fail-closed placeholder awaiting a ruling: the decision that records it. */
  provisional?: string;
  /** Set when the notice is due only in some cases (the SIRS additional notice). */
  conditional?: string;
}

/** A rule applied to a record: its due instant (UTC `…Z`, seconds). */
export interface DeadlineRule extends DeadlineRuleSpec {
  dueAt: string;
  /** Set when a value the rule needs was not chosen yet and the stricter one was assumed (D-13-27-6). */
  assumed?: string;
}

/** A final report's due day (`YYYY-MM-DD`, a Melbourne day for the `…On` column). */
export interface FinalReportDue extends DeadlineRuleSpec {
  dueOn: string;
}

/** What the deadline is computed from. Choice values are the live ones, verbatim. */
export interface DueInput {
  /** The stored `AwareAt` instant (zoned; a zoneless value is never read). */
  awareAt: string | null | undefined;
  /** `ReportabilityDecision`. */
  decision: string | null | undefined;
  /**
   * `SirsKind`. Accepted for the plan's signature; the SIRS period turns on the priority, which is the decision
   * itself (`SIRS-P1` / `SIRS-P2`), not on the kind (OB-AC-61).
   */
  sirsKind?: string | null;
  /** `NdisNoticeClass`. */
  ndisNoticeClass?: string | null;
  /** `WorkSafeNotifiable` (the WHS verb's answer). `Yes` adds the WorkSafe regime whatever the decision. */
  workSafeNotifiable?: string | null;
}

export interface DueResult {
  /** The earliest due instant of every regime that applies, or `null` when none applies (never a default). */
  dueAt: string | null;
  /** Every regime that contributed, decision first, then WorkSafe. */
  regimes: Regime[];
  /** One rule per regime (the initial notice), with its due instant. */
  rules: DeadlineRule[];
  /** A regime applies but its clock can't start: `AwareAt` is not recorded (or is unreadable). */
  missing?: 'aware-at';
}

// ---------------------------------------------------------------------------
// The rules (each from its 12.1-OBLIGATIONS.md row)
// ---------------------------------------------------------------------------

const HOLIDAYS =
  'Public holidays are not skipped (the app has no public-holiday calendar), so the due date may be early, never late.';

const SPECS: Readonly<Record<RuleKey, DeadlineRuleSpec>> = {
  // OB-AC-64 · ACR-2025-s165A-25 (1): "within 24 hours of becoming aware of the reportable incident"
  'sirs-p1': {
    key: 'sirs-p1',
    regime: 'sirs',
    notice: 'SirsNoticeAt',
    obRow: 'OB-AC-64',
    alsoRows: ['OB-AC-65', 'OB-AC-40'],
    section: 'ACR-2025-s165A-25 (1)',
    period: 'within 24 hours of becoming aware',
    reading: '24 hours after Harmony became aware',
  },
  // OB-AC-66 · ACR-2025-s165A-30 (1): "within 30 days of becoming aware of the incident"
  'sirs-p2': {
    key: 'sirs-p2',
    regime: 'sirs',
    notice: 'SirsNoticeAt',
    obRow: 'OB-AC-66',
    alsoRows: ['OB-AC-67', 'OB-AC-40'],
    section: 'ACR-2025-s165A-30 (1)',
    period: 'within 30 days of becoming aware',
    reading: 'the earliest reading of 30 days after Harmony became aware (counting the day of awareness as day 1)',
    provisional: 'D-13-27-2: the earliest of three readings of "within 30 days of"; a ruling can move it later, never earlier',
  },
  // OB-AC-81 · ACR-2025-s165A-25 (6)-(7): "within 5 days after the start of the 24 hours"
  'sirs-p1-additional': {
    key: 'sirs-p1-additional',
    regime: 'sirs',
    notice: 'SirsAdditionalNoticeAt',
    obRow: 'OB-AC-81',
    alsoRows: ['OB-AC-80'],
    section: 'ACR-2025-s165A-25 (6)-(7)',
    period: 'within 5 days after the start of the 24 hours',
    reading: '5 days after Harmony became aware (the earlier of 120 hours and the same Melbourne time)',
    conditional:
      'Only if information was left out of the P1 notice (OB-AC-80), and unless the Commissioner set another period or decided it is not needed.',
  },
  // OB-AC-86 · ACR-2025-s165A-45 (2): "within 84 days of the day a notice about the incident was first given"
  'sirs-final-report': {
    key: 'sirs-final-report',
    regime: 'sirs',
    notice: 'FinalReportGivenAt',
    obRow: 'OB-AC-86',
    alsoRows: ['OB-AC-85', 'OB-AC-87'],
    section: 'ACR-2025-s165A-45 (2)',
    period: '84 days',
    reading: '84 days of the day the first notice was given, counting that day as day 1 (or the period the Commissioner specifies)',
    provisional: 'D-13-27-8: "within 84 days of the day" read with the notice day as day 1, the earlier of the two counts',
  },
  // OB-NDIS-47 · NDIS-IMRI-RULES-2018 s 20(2): "within 24 hours" of becoming aware (the s 20(1)(b) kinds)
  'ndis-24h': {
    key: 'ndis-24h',
    regime: 'ndis',
    notice: 'NdisInitialNoticeAt',
    obRow: 'OB-NDIS-47',
    alsoRows: ['OB-NDIS-48', 'OB-NDIS-46', 'OB-NDIS-43'],
    section: 'NDIS-IMRI-RULES-2018 s 20(2)',
    period: 'within 24 hours of becoming aware',
    reading: '24 hours after Harmony became aware',
  },
  // OB-NDIS-62 · NDIS-IMRI-RULES-2018 s 20(4): after a 24-hour notification, the written notice within 5 business days
  'ndis-24h-further': {
    key: 'ndis-24h-further',
    regime: 'ndis',
    notice: 'NdisFurtherNoticeAt',
    obRow: 'OB-NDIS-62',
    alsoRows: ['OB-NDIS-59', 'OB-NDIS-60', 'OB-NDIS-61'],
    section: 'NDIS-IMRI-RULES-2018 s 20(4)',
    period: 'within 5 business days after becoming aware',
    reading: '5 business days after Harmony became aware, at the same Melbourne time',
    note: HOLIDAYS,
  },
  // OB-NDIS-62 · NDIS-IMRI-RULES-2018 s 21(2): every other reportable incident, in writing within 5 business days
  'ndis-5bd': {
    key: 'ndis-5bd',
    regime: 'ndis',
    notice: 'NdisFurtherNoticeAt',
    obRow: 'OB-NDIS-62',
    alsoRows: ['OB-NDIS-46', 'OB-NDIS-43'],
    section: 'NDIS-IMRI-RULES-2018 s 21(2)',
    period: 'within 5 business days after becoming aware',
    reading: '5 business days after Harmony became aware, at the same Melbourne time',
    note: HOLIDAYS,
  },
  // OB-NDIS-67 · NDIS-IMRI-RULES-2018 s 24(1): "within 60 business days after the notification"
  'ndis-final-report': {
    key: 'ndis-final-report',
    regime: 'ndis',
    notice: 'FinalReportGivenAt',
    obRow: 'OB-NDIS-67',
    alsoRows: ['OB-NDIS-66', 'OB-NDIS-68'],
    section: 'NDIS-IMRI-RULES-2018 s 24(1)',
    period: '60 business days',
    reading: '60 business days after the earliest NDIS notice given (or the longer period the Commissioner specifies)',
    note: HOLIDAYS,
  },
  // OB-OHS-05 · OHSA-VIC-2004-NOTIF s 38(1): "notify the Authority immediately" after becoming aware
  worksafe: {
    key: 'worksafe',
    regime: 'worksafe',
    notice: 'WorkSafeNotifiedAt',
    obRow: 'OB-OHS-05',
    alsoRows: ['OB-OHS-06', 'OB-OHS-04'],
    section: 'OHSA-VIC-2004-NOTIF s 38(1)',
    period: 'immediately after becoming aware',
    reading: 'immediately: the moment Harmony became aware',
  },
  // OB-OHS-09 · OHSA-VIC-2004-NOTIF s 38(3): the written record "within 48 hours after being required to notify"
  'worksafe-written-record': {
    key: 'worksafe-written-record',
    regime: 'worksafe',
    notice: 'WorkSafeWrittenRecordAt',
    obRow: 'OB-OHS-09',
    alsoRows: ['OB-OHS-10'],
    section: 'OHSA-VIC-2004-NOTIF s 38(3)',
    period: 'within 48 hours after being required to notify',
    reading: '48 hours after the duty to notify arose, that is, from the earlier of when Harmony became aware and the notice',
    provisional: 'D-13-27-9: anchored at the duty to notify (AwareAt), not at the notice the plan names; the earlier',
  },
  // 13-28, the WHS verb's day columns (WIRC Act 2013 (Vic)). Each anchor is a day column, so each due is a Melbourne day.
  // OB-OHS-33 · WIRCA-VIC-2013 s 73(1): forward the claim "within 10 days after receipt"
  'whs-claim-forward': {
    key: 'whs-claim-forward',
    regime: 'whs',
    notice: 'ClaimForwardedOn',
    obRow: 'OB-OHS-33',
    alsoRows: ['OB-OHS-34', 'OB-OHS-29'],
    section: 'WIRCA-VIC-2013 s 73(1)',
    period: 'within 10 days after receipt',
    reading: '10 calendar days after the day the claim was received, that day not counted',
    note: 'A period that ends on a weekend or public holiday is not extended, so the day may be early, never late.',
    provisional: 'D-13-28-7: read as calendar days with no extension when the tenth day is a weekend or holiday, the earliest reading; a ruling can move it later, never earlier',
  },
  // OB-OHS-35 · WIRCA-VIC-2013 s 73(1)(b): forward the medical certificate "within 10 days" (with or after the claim)
  'whs-medical-certificate-forward': {
    key: 'whs-medical-certificate-forward',
    regime: 'whs',
    notice: 'MedicalCertificateForwardedOn',
    obRow: 'OB-OHS-35',
    alsoRows: ['OB-OHS-29'],
    section: 'WIRCA-VIC-2013 s 73(1)(b)',
    period: 'within 10 days',
    reading:
      'the earlier of 10 days after the claim was received and 10 days after the certificate was received, and no earlier than the day the certificate arrived',
    provisional: 'D-13-28-3: the row names no anchor for the 10 days, so the earlier anchor is used; a ruling can move it later, never earlier',
  },
  // OB-OHS-37 · WIRCA-VIC-2013 s 73A(1): notify a mental injury claim "within 3 business days after receipt"
  'whs-mental-injury-notify': {
    key: 'whs-mental-injury-notify',
    regime: 'whs',
    notice: 'MentalInjuryNotifiedOn',
    obRow: 'OB-OHS-37',
    alsoRows: ['OB-OHS-36', 'OB-OHS-29'],
    section: 'WIRCA-VIC-2013 s 73A(1)',
    period: 'within 3 business days after receipt',
    reading: '3 business days after the day the claim was received, Melbourne Monday to Friday',
    note: HOLIDAYS,
  },
  // OB-OHS-39 · WIRCA-VIC-2013 s 104(1): plan return to work "from that date" (when Harmony knew of the incapacity)
  'whs-rtw-planning': {
    key: 'whs-rtw-planning',
    regime: 'whs',
    notice: 'RtwPlanningStartedOn',
    obRow: 'OB-OHS-39',
    section: 'WIRCA-VIC-2013 s 104(1)',
    period: 'from that date',
    reading: 'planning is due to start on the day Harmony knew, or ought reasonably to have known, of the incapacity',
    provisional: 'D-13-28-4: the row gives no period, only "from that date", so the start is read as due that day; a ruling can move it later, never earlier',
  },
  // No OB row: the `Other` decision names no regime the matrix covers. A fail-closed placeholder.
  other: {
    key: 'other',
    regime: 'other',
    notice: null,
    obRow: null,
    section: 'no rule in 12.1-OBLIGATIONS.md',
    period: '—',
    reading: 'no rule covers "Other", so it is due from the moment Harmony became aware until Oversight rules',
    provisional: 'D-13-27-6: a fail-closed placeholder at AwareAt; override it with the regulator’s period and a reason',
  },
};

/** Every rule, for the citation check and the panels' "how this is worked out" lines. */
export const DEADLINE_RULES: readonly DeadlineRuleSpec[] = Object.values(SPECS);

// ---------------------------------------------------------------------------
// Melbourne calendar arithmetic (zone-free on YYYY-MM-DD text; instants through fieldValues)
// ---------------------------------------------------------------------------

const HOUR_MS = 3_600_000;
const DAY_MS = 24 * HOUR_MS;

/** A Melbourne calendar day `n` days on (UTC arithmetic on the date only, so no zone is involved). */
function addDays(day: string, n: number): string {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}

/** 0 = Sunday … 6 = Saturday, for a calendar day. */
function weekdayOf(day: string): number {
  const [y, m, d] = day.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}

/** The `n`th Monday-to-Friday day after `day` (public holidays counted, D-13-27-1). */
function addBusinessDays(day: string, n: number): string {
  let d = day;
  for (let counted = 0; counted < n; ) {
    d = addDays(d, 1);
    const w = weekdayOf(d);
    if (w !== 0 && w !== 6) counted += 1;
  }
  return d;
}

/** Calendar days from `a` to `b`. */
function daysBetween(a: string, b: string): number {
  const [ay, am, ad] = a.split('-').map(Number);
  const [by, bm, bd] = b.split('-').map(Number);
  return Math.round((Date.UTC(by, bm - 1, bd) - Date.UTC(ay, am - 1, ad)) / DAY_MS);
}

/** `HH:MM` less `n` minutes, on the same day (n < the minutes since midnight). */
function minutesBefore(time: string, n: number): string {
  const [h, m] = time.split(':').map(Number);
  const total = h * 60 + m - n;
  return `${String(Math.floor(total / 60)).padStart(2, '0')}:${String(total % 60).padStart(2, '0')}`;
}

/**
 * A Melbourne wall time as epoch ms, by the `Australia/Melbourne` rules. The repeated hour gives its first
 * occurrence (the earlier, `melbourneWallToInstant`). A time in the skipped hour gives the moment the clocks jumped,
 * which is earlier than any reading of a time that never happened.
 */
function wallMs(day: string, time: string, seconds: number): number {
  const r = melbourneWallToInstant(day, time);
  if (r.ok) return Date.parse(r.value) + seconds * 1000;
  if (r.code === 'nonexistent-time') {
    for (let back = 1; back <= 120; back += 1) {
      const earlier = melbourneWallToInstant(day, minutesBefore(time, back));
      if (earlier.ok) return Date.parse(earlier.value) + 60_000; // the last minute that existed, + 1 min = the jump
    }
  }
  throw new Error(`notificationDeadlines: no Melbourne instant for ${day} ${time}`);
}

/** The last second of a Melbourne day (midnight always exists in Melbourne: the clocks change at 2 to 3 am). */
function endOfDayMs(day: string): number {
  return wallMs(addDays(day, 1), '00:00', 0) - 1000;
}

interface Aware {
  ms: number;
  day: string;
  time: string;
  seconds: number;
}

function readInstant(value: string | null | undefined): Aware | null {
  if (typeof value !== 'string' || value.trim() === '') return null;
  const wall = melbourneWallOf(value);
  if (!wall) return null;
  return { ms: Date.parse(value), ...wall };
}

/**
 * "N days" from an instant, earliest reading (D-13-27-2): N × 24 elapsed hours; the same Melbourne clock time N days
 * on; and, for a period worded "within N days **of**" (not "after"), the end of day N counting the first day as day 1.
 */
function daysAfterMs(a: Aware, n: number, opts: { firstDayCounts: boolean }): number {
  const candidates = [a.ms + n * DAY_MS, wallMs(addDays(a.day, n), a.time, a.seconds)];
  if (opts.firstDayCounts) candidates.push(endOfDayMs(addDays(a.day, n - 1)));
  return Math.min(...candidates);
}

/** "N business days after" an instant: the same Melbourne clock time on the Nth business day, or the elapsed days if earlier. */
function businessDaysAfterMs(a: Aware, n: number): number {
  const target = addBusinessDays(a.day, n);
  return Math.min(wallMs(target, a.time, a.seconds), a.ms + daysBetween(a.day, target) * DAY_MS);
}

/** The due instant of an AwareAt-anchored rule. */
function dueMs(key: RuleKey, a: Aware): number {
  switch (key) {
    case 'sirs-p1':
    case 'ndis-24h':
      return a.ms + 24 * HOUR_MS; // 24 elapsed hours, even across a DST change
    case 'sirs-p2':
      return daysAfterMs(a, 30, { firstDayCounts: true }); // "within 30 days of"
    case 'sirs-p1-additional':
      return daysAfterMs(a, 5, { firstDayCounts: false }); // "within 5 days after the start of"
    case 'ndis-24h-further':
    case 'ndis-5bd':
      return businessDaysAfterMs(a, 5);
    case 'worksafe':
    case 'other':
      return a.ms; // "immediately"; the placeholder
    case 'worksafe-written-record':
      return a.ms + 48 * HOUR_MS;
    default:
      throw new Error(`notificationDeadlines: ${key} is not anchored at AwareAt`);
  }
}

function applied(key: RuleKey, a: Aware, assumed?: string): DeadlineRule {
  return { ...SPECS[key], dueAt: toInstant(new Date(dueMs(key, a))), ...(assumed ? { assumed } : {}) };
}

const textOf = (value: unknown): string => (typeof value === 'string' ? value.trim() : '');

// ---------------------------------------------------------------------------
// Which rules apply
// ---------------------------------------------------------------------------

interface Applies {
  /** The decision's rule, then WorkSafe's. */
  keys: RuleKey[];
  assumed?: string;
}

const ASSUMED_24H = 'No NDIS notification class is chosen yet, so the stricter 24 hours is assumed until it is.';

function applies(input: DueInput): Applies {
  const decision = textOf(input.decision);
  const keys: RuleKey[] = [];
  let assumed: string | undefined;
  if (decision === SIRS_P1) keys.push('sirs-p1');
  else if (decision === SIRS_P2) keys.push('sirs-p2');
  else if (decision === NDIS_REPORTABLE) {
    const cls = textOf(input.ndisNoticeClass);
    if (cls === CLASS_5BD) keys.push('ndis-5bd');
    else {
      keys.push('ndis-24h');
      if (cls !== CLASS_24H) assumed = ASSUMED_24H;
    }
  } else if (decision === WORKSAFE) keys.push('worksafe');
  else if (decision !== '' && !NO_DEADLINE_DECISIONS.includes(decision)) keys.push('other'); // `Other`, or a value no rule knows
  // OB-OHS-05: the WorkSafe duty is the WHS verb's answer, independent of the reportability decision (D-13-27-12).
  if (textOf(input.workSafeNotifiable) === WORKSAFE_YES && !keys.includes('worksafe')) keys.push('worksafe');
  return { keys, ...(assumed ? { assumed } : {}) };
}

// ---------------------------------------------------------------------------
// The API
// ---------------------------------------------------------------------------

/**
 * `NotificationDueAt` for a record: the earliest initial-notice due instant of every regime that applies, with the
 * rules that gave it. `Pending`, `NotReportable` or a blank decision (and no WorkSafe answer of `Yes`) → `null`.
 */
export function notificationDue(input: DueInput): DueResult {
  const { keys, assumed } = applies(input);
  const regimes = [...new Set(keys.map((k) => SPECS[k].regime))];
  if (keys.length === 0) return { dueAt: null, regimes: [], rules: [] };
  const aware = readInstant(input.awareAt);
  if (!aware) return { dueAt: null, regimes, rules: [], missing: 'aware-at' };
  const rules = keys.map((k) => applied(k, aware, k === 'ndis-24h' ? assumed : undefined));
  const earliest = Math.min(...rules.map((r) => Date.parse(r.dueAt)));
  return { dueAt: toInstant(new Date(earliest)), regimes, rules };
}

/** Each notice's due instant, for the notification panel: the initial notice and its follow-ups, per regime. */
export function noticeDeadlines(input: DueInput & { notices?: Readonly<Record<string, unknown>> }): DeadlineRule[] {
  const { keys, assumed } = applies(input);
  const aware = readInstant(input.awareAt);
  if (!aware) return [];
  const out: DeadlineRule[] = [];
  for (const key of keys) {
    switch (key) {
      case 'ndis-24h':
        out.push(applied('ndis-24h', aware, assumed), applied('ndis-24h-further', aware));
        break;
      case 'sirs-p1':
        out.push(applied('sirs-p1', aware), applied('sirs-p1-additional', aware));
        break;
      case 'worksafe': {
        out.push(applied('worksafe', aware));
        const written = worksafeWrittenRecordDue(textOf(input.notices?.WorkSafeNotifiedAt) || null, input.awareAt ?? null);
        if (written) out.push(written);
        break;
      }
      default:
        out.push(applied(key, aware));
    }
  }
  return out;
}

/**
 * The WorkSafe written record's due instant (for 13-28): 48 hours after the duty to notify arose. The duty arises
 * "immediately after becoming aware" (OB-OHS-04 / -05), so the anchor is the earlier of `AwareAt` and the notice
 * (`notifiedAt`), never only the notice (provisional, D-13-27-9). `null` when neither is recorded.
 */
export function worksafeWrittenRecordDue(notifiedAt: string | null, awareAt: string | null): DeadlineRule | null {
  const anchors = [readInstant(notifiedAt), readInstant(awareAt)].filter((a): a is Aware => a !== null);
  if (anchors.length === 0) return null;
  const anchor = anchors.reduce((a, b) => (b.ms < a.ms ? b : a));
  return applied('worksafe-written-record', anchor);
}

// ---------------------------------------------------------------------------
// 13-28: the WHS verb's day deadlines (WIRC Act), each a Melbourne day
// ---------------------------------------------------------------------------

const [MENTAL_YES] = liveChoice('incidents', 'MentalInjuryClaim', 'Yes');

/** A WHS day deadline: due on a Melbourne day (`YYYY-MM-DD`), from the day column named in `anchor`. */
export interface DayDeadline extends DeadlineRuleSpec {
  dueOn: string;
  /** The day column(s) the period runs from. */
  anchor: readonly string[];
}

/** The WHS verb's anchor columns, as stored (a day, or an instant read as its Melbourne day) or as typed. */
export interface WhsDayInput {
  ClaimReceivedOn?: string | null;
  MedicalCertificateReceivedOn?: string | null;
  MentalInjuryClaim?: string | null;
  RtwKnowledgeOn?: string | null;
}

function dayOf(value: string | null | undefined): string | null {
  return typeof value === 'string' && value.trim() !== '' ? melbourneDayOf(value.trim()) : null;
}

/**
 * The "by" day of each WHS duty whose anchor is recorded (13-28): forward the claim (OB-OHS-33), forward the medical
 * certificate (OB-OHS-35, provisional D-13-28-3), notify a mental injury claim (OB-OHS-37) and start RTW planning
 * (OB-OHS-39, provisional D-13-28-4). Calendar arithmetic is on Melbourne days; business days count public holidays
 * (D-13-27-1). Nothing is due without its anchor.
 */
export function whsDayDeadlines(input: WhsDayInput): DayDeadline[] {
  const claim = dayOf(input.ClaimReceivedOn);
  const cert = dayOf(input.MedicalCertificateReceivedOn);
  const knew = dayOf(input.RtwKnowledgeOn);
  const out: DayDeadline[] = [];
  if (claim) out.push({ ...SPECS['whs-claim-forward'], dueOn: addDays(claim, 10), anchor: ['ClaimReceivedOn'] });
  if (cert) {
    // The earlier anchor + 10 days, but never before the certificate itself arrived (it can't be forwarded sooner).
    const tenDays = [addDays(cert, 10), ...(claim ? [addDays(claim, 10)] : [])].sort()[0];
    const dueOn = tenDays < cert ? cert : tenDays;
    out.push({ ...SPECS['whs-medical-certificate-forward'], dueOn, anchor: claim ? ['ClaimReceivedOn', 'MedicalCertificateReceivedOn'] : ['MedicalCertificateReceivedOn'] });
  }
  if (claim && textOf(input.MentalInjuryClaim) === MENTAL_YES) {
    out.push({ ...SPECS['whs-mental-injury-notify'], dueOn: addBusinessDays(claim, 3), anchor: ['ClaimReceivedOn'] });
  }
  if (knew) out.push({ ...SPECS['whs-rtw-planning'], dueOn: knew, anchor: ['RtwKnowledgeOn'] });
  return out;
}

/**
 * The final report's due day, pre-filled when the Commissioner requires one (editable: the Commissioner may specify
 * another period). SIRS: 84 days of the day the first SIRS notice was given, that day counted as day 1 (provisional,
 * D-13-27-8). NDIS: 60 business days after the earliest NDIS notice given. `null` when no notice is recorded yet.
 */
export function finalReportDueOn(input: { regime: 'sirs' | 'ndis'; notices: Readonly<Record<string, unknown>> }): FinalReportDue | null {
  const columns = input.regime === 'sirs' ? ['SirsNoticeAt', 'SirsAdditionalNoticeAt'] : ['NdisInitialNoticeAt', 'NdisFurtherNoticeAt'];
  const days = columns
    .map((c) => textOf(input.notices[c]))
    .filter((v) => v !== '')
    .map((v) => melbourneDayOf(v))
    .filter((d): d is string => d !== null)
    .sort();
  if (days.length === 0) return null;
  const first = days[0];
  return input.regime === 'sirs'
    ? { ...SPECS['sirs-final-report'], dueOn: addDays(first, 83) } // OB-AC-86 · ACR-2025-s165A-45 (2)
    : { ...SPECS['ndis-final-report'], dueOn: addBusinessDays(first, 60) }; // OB-NDIS-67 · NDIS-IMRI-RULES-2018 s 24(1)
}

/** The shortest override reason (D-13-L-11; the closure verbs' 10, D-13-21-7). */
export const MIN_OVERRIDE_REASON = 10;

export type OverrideResult =
  | { ok: true; dueAt: string; note: string }
  | { ok: false; code: 'reason-required' | 'invalid-date' | 'no-deadline' };

/**
 * A hand-set `NotificationDueAt` in place of the computed one (D-13-L-11), e.g. a period the Commissioner set. Needs
 * a reason of at least 10 characters, and returns the audit Note text for the column's `StateChange` row:
 * `NotificationDueAt overridden (computed <D MMM YYYY h:mm am>): <reason>`.
 */
export function overrideDue(computed: DueResult | string | null, manualIso: string, reason: string): OverrideResult {
  const why = cleanText(typeof reason === 'string' ? reason : '', 4000);
  if (!why.ok || why.value.length < MIN_OVERRIDE_REASON) return { ok: false, code: 'reason-required' };
  const computedAt = typeof computed === 'string' ? computed : (computed?.dueAt ?? null);
  if (!computedAt || melbourneDayOf(computedAt) === null) return { ok: false, code: 'no-deadline' };
  const manual = readInstant(manualIso);
  if (!manual) return { ok: false, code: 'invalid-date' };
  return {
    ok: true,
    dueAt: toInstant(new Date(manual.ms)),
    note: `NotificationDueAt overridden (computed ${formatInstant(computedAt).replace(', ', ' ')}): ${why.value}`,
  };
}
